import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lock, Mail, Send, Sparkles, TrendingUp, DollarSign, Check, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  html_content: string;
  template_type: string;
  target_audience?: string;
  is_active: boolean;
  created_at: string;
}

interface EmailTemplate {
  id: string;
  title: string;
  subject: string;
  content: string;
  category: string;
  is_active: boolean;
}

const AffiliateEmailAutomation = () => {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    subject: "",
    html_content: "",
    template_type: "affiliate_promo",
  });

  useEffect(() => {
    checkAccess();
    loadTemplates();
    
    // Check for success parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      toast.success("Payment successful! Activating your access...");
      // Refresh access check after a short delay
      setTimeout(() => {
        checkAccess();
      }, 2000);
    }
  }, []);

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .eq("category", "affiliate")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    }
  };

  const checkAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase
        .from("affiliate_email_access" as any)
        .select("*")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setHasAccess(true);
        loadCampaigns();
      }
    } catch (error) {
      console.error("Error checking access:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      // @ts-ignore - Using any to avoid type instantiation depth issues
      const result = await supabase
        .from("email_campaigns")
        .select("*")
        .eq("target_audience", "affiliate")
        .order("created_at", { ascending: false });

      if (result.error) throw result.error;
      setCampaigns(result.data || []);
    } catch (error) {
      console.error("Error loading campaigns:", error);
      toast.error("Failed to load campaigns");
    }
  };

  const handlePurchaseAccess = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please sign in to purchase access");
        navigate("/auth");
        return;
      }

      toast.loading("Redirecting to payment...");

      const { data, error } = await supabase.functions.invoke("create-affiliate-payment", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error("Error creating payment:", error);
      toast.error(error.message || "Failed to create payment session");
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name || !newCampaign.subject || !newCampaign.html_content) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("email_campaigns")
        .insert({
          name: newCampaign.name,
          subject: newCampaign.subject,
          html_content: newCampaign.html_content,
          template_type: newCampaign.template_type,
          target_audience: "affiliate",
          is_active: true,
        });

      if (error) throw error;

      toast.success("Campaign created successfully!");
      setNewCampaign({
        name: "",
        subject: "",
        html_content: "",
        template_type: "affiliate_promo",
      });
      loadCampaigns();
    } catch (error: any) {
      console.error("Error creating campaign:", error);
      toast.error(error.message || "Failed to create campaign");
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-xl">Loading...</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background p-8">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-8"
          >
            ← Back to Dashboard
          </Button>

          <Card className="border-2 border-primary/20">
            <CardHeader className="text-center space-y-4">
              <div className="w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto">
                <Lock className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-3xl">Affiliate Email Automation</CardTitle>
              <CardDescription className="text-lg">
                Unlock premium email templates and automation for your affiliate business
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      High-Value Email Templates
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>Launch announcements that convert</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>Flash sale templates with urgency</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>Case study storytelling formats</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>Value-first newsletter features</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      Campaign Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>Create unlimited campaigns</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>Automated follow-up sequences</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>Performance tracking & analytics</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 mt-0.5" />
                      <span>A/B testing capabilities</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold">Lifetime Access</h3>
                      <p className="text-muted-foreground">One-time purchase, unlimited use</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-primary">$50</div>
                      <Badge variant="secondary">One-time payment</Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <Button
                      onClick={handlePurchaseAccess}
                      className="w-full"
                      size="lg"
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      Purchase Lifetime Access
                    </Button>
                    <p className="text-sm text-muted-foreground text-center">
                      Secure payment powered by Stripe • 30-day money-back guarantee
                    </p>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="mb-4"
            >
              ← Back to Dashboard
            </Button>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Affiliate Email Automation
            </h1>
            <p className="text-muted-foreground mt-2">
              Create high-converting email campaigns for your audience
            </p>
          </div>
          <Badge variant="default" className="h-fit">
            <DollarSign className="w-4 h-4 mr-1" />
            Premium Access
          </Badge>
        </div>

        {/* High-Value Email Prompts */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            High-Value Email Templates
          </h2>
          <div className="grid gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{template.title}</CardTitle>
                      <CardDescription className="mt-2">
                        Subject: {template.subject}
                      </CardDescription>
                    </div>
                    <Button
                      onClick={() => {
                        setNewCampaign({
                          name: template.title,
                          subject: template.subject,
                          html_content: template.content,
                          template_type: "affiliate_promo",
                        });
                        toast.success("Template loaded! Customize and create below.");
                      }}
                      size="sm"
                    >
                      Use Template
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre className="text-sm bg-muted p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
                    {template.content}
                  </pre>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Create New Campaign */}
        <Card className="border-2 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Create New Campaign
            </CardTitle>
            <CardDescription>
              Customize your email campaign and launch it to your audience
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Campaign Name</label>
              <Input
                placeholder="e.g., Spring Launch Promotion"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Email Subject Line</label>
              <Input
                placeholder="e.g., 🚀 You're Invited: Exclusive Launch Partner Opportunity"
                value={newCampaign.subject}
                onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Email Content</label>
              <Textarea
                placeholder="Write your email content here..."
                value={newCampaign.html_content}
                onChange={(e) => setNewCampaign({ ...newCampaign, html_content: e.target.value })}
                rows={12}
              />
            </div>
            <Button onClick={handleCreateCampaign} className="w-full" size="lg">
              <Send className="w-5 h-5 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>

        {/* Campaign List */}
        {campaigns.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Your Campaigns</h2>
            <div className="grid gap-4">
              {campaigns.map((campaign) => (
                <Card key={campaign.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{campaign.name}</CardTitle>
                        <CardDescription>{campaign.subject}</CardDescription>
                      </div>
                      <Badge variant={campaign.is_active ? "default" : "secondary"}>
                        {campaign.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateEmailAutomation;
